// Response + request types for the referral-codes admin endpoints. Field names
// match the Lambda payloads in socialspark-admin-infrastructure exactly
// (backend/lambdas/referrals/admin/*, shapes built by shared/referrals.py).

export type CodeKind = 'personal' | 'promoter' | 'campaign';
/** Only promoter / campaign are mintable here — personal codes are minted lazily by the consumer. */
export type CreatableCodeKind = 'promoter' | 'campaign';
export type CodeStatus = 'active' | 'disabled';

export type CampaignKind = 'campaign' | 'personal_default';
export type CampaignStatus = 'draft' | 'active' | 'ended';
/** 'ended' is reached by editing, never by creating (post-admin-promo-campaign). */
export type CampaignCreateStatus = 'draft' | 'active';

/** $1,000 per signup — PAYOUT_RATE_MAX_CENTS in shared/referrals.py. */
export const PAYOUT_RATE_MAX_CENTS = 100_000;
/** MAX_CLAIMS_MAX in post/put-admin-promo-campaign. */
export const MAX_CLAIMS_MAX = 1_000_000;
/** MAX_PAGE_SIZE / DEFAULT_PAGE_SIZE in shared/referrals.py. */
export const MAX_PAGE_SIZE = 200;
export const DEFAULT_PAGE_SIZE = 50;
/** COINS_MAX in shared/referrals.py — the bound put-admin-referral-settings enforces. */
export const COINS_MAX = 10_000;

/** norm_code(): 3-32 chars of A-Z, 0-9, '-' or '_', starting alphanumeric. */
export const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,31}$/;

// --- Codes -------------------------------------------------------------------

export interface CodeOwner {
  user_id: string;
  display_name: string | null;
  email: string | null;
}

export interface CodeDealCampaign {
  id: string;
  name: string | null;
  kind: CampaignKind | null;
  status: CampaignStatus | null;
}

/**
 * Per-code rollup over the caller's date window. Definitions live in
 * shared/referrals.py — `owed_cents` sums the payout rate SNAPSHOT on each
 * attribution row (payout-eligible, non-flagged, non-void), so editing a code's
 * rate is never retroactive.
 */
export interface CodeStats {
  signups_total: number;
  signups_verified: number;
  review_flagged_count: number;
  owed_cents: number;
  owed_verified_cents: number;
  granted_deal_count: number;
  redeemed_deal_count: number;
}

export interface ReferralCode {
  code: string;
  kind: CodeKind;
  status: CodeStatus;
  owner: CodeOwner | null;
  deal_campaign: CodeDealCampaign | null;
  payout_rate_cents: number | null;
  ambassador_ref: string | null;
  created_by: string | null;
  created_at: string;
  stats: CodeStats;
}

/** The half-open [from, to) window the aggregate was computed over. */
export interface DateWindow {
  from: string | null;
  to: string | null;
}

export interface CodePageTotals {
  signups_total: number;
  signups_verified: number;
  owed_cents: number;
  owed_verified_cents: number;
  redeemed_deal_count: number;
  review_flagged_count: number;
}

export interface ReferralCodesResponse {
  codes: ReferralCode[];
  total: number;
  limit: number;
  offset: number;
  window: DateWindow;
  page_totals: CodePageTotals;
}

/**
 * One referral_attributions row. `device_id` / `source_ip` are present only on
 * review-flagged rows — they are what the flag review is for.
 */
export interface AttributionRow {
  id: string;
  /** NULL once the referred account is deleted (V102 uses ON DELETE SET NULL). */
  new_user_id: string | null;
  display_name: string | null;
  email: string | null;
  kind: string;
  status: string;
  entered_via: string | null;
  payout_eligible: boolean;
  payout_rate_cents: number | null;
  review_flag: string | null;
  device_id?: string | null;
  source_ip?: string | null;
  counted_at: string;
  synced_to_crm_at: string | null;
}

export interface DealGrantRow {
  id: string;
  user_id: string | null;
  display_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  status: string;
  granted_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  redemption_id: string | null;
}

export interface ReferralCodeDetail extends ReferralCode {
  window: DateWindow;
  review_flagged: AttributionRow[];
  review_flagged_truncated: boolean;
  recent_signups: AttributionRow[];
  recent_signups_truncated: boolean;
  deal_grants: DealGrantRow[];
}

/** Whitelisted `sort` values for the codes list (CODE_SORT_KEYS). */
export type CodeSortKey =
  | 'code'
  | 'kind'
  | 'status'
  | 'created_at'
  | 'payout_rate'
  | 'signups'
  | 'verified_signups'
  | 'owed'
  | 'redeemed'
  | 'flagged';

export interface CodeFilters {
  from?: string;
  to?: string;
  kind?: CodeKind | '';
  status?: CodeStatus | '';
  campaign_id?: string;
  owner?: string;
  q?: string;
  sort?: CodeSortKey;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateCodeBody {
  kind: CreatableCodeKind;
  code?: string;
  owner_user_id?: string;
  owner_email?: string;
  payout_rate_cents?: number;
  deal_campaign_id?: string;
  ambassador_ref?: string;
  status?: CodeStatus;
  reason?: string;
}

/**
 * Partial update — only keys present change; an explicit `null` clears a
 * nullable field. `code` and `kind` are immutable by design.
 */
export interface UpdateCodeBody {
  owner_user_id?: string | null;
  owner_email?: string;
  payout_rate_cents?: number | null;
  deal_campaign_id?: string | null;
  ambassador_ref?: string | null;
  status?: CodeStatus;
  reason?: string;
}

// --- Campaigns ---------------------------------------------------------------

export interface EligibleBusiness {
  business_id: string;
  name: string | null;
}

export interface PromoCampaign {
  id: string;
  kind: CampaignKind;
  name: string;
  deal_description: string;
  template_key: string | null;
  starts_at: string | null;
  ends_at: string | null;
  max_claims: number | null;
  claim_count: number;
  /** Slots left under the first-N cap; null when uncapped. */
  claims_remaining: number | null;
  status: CampaignStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  eligible_businesses: EligibleBusiness[];
  /** List response only. */
  grants?: { granted: number; redeemed: number };
  /** PUT response only — set when the resulting state cannot grant. */
  warnings?: string[];
}

export interface PromoCampaignsResponse {
  campaigns: PromoCampaign[];
  total: number;
  limit: number;
  offset: number;
}

export type CampaignSortKey = 'name' | 'status' | 'created_at' | 'starts_at' | 'ends_at' | 'claims';

export interface CampaignFilters {
  status?: CampaignStatus | '';
  kind?: CampaignKind | '';
  q?: string;
  sort?: CampaignSortKey;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateCampaignBody {
  name: string;
  deal_description: string;
  starts_at: string;
  ends_at: string;
  max_claims?: number | null;
  business_ids?: string[];
  template_key?: string | null;
  status?: CampaignCreateStatus;
  reason?: string;
}

/** Partial update. `business_ids`, when supplied, REPLACES the eligible set. */
export interface UpdateCampaignBody {
  name?: string;
  deal_description?: string;
  starts_at?: string;
  ends_at?: string;
  max_claims?: number | null;
  business_ids?: string[];
  template_key?: string | null;
  status?: CampaignStatus;
  reason?: string;
}

// --- Settings ----------------------------------------------------------------

/**
 * How many live codes ride on the fallback deal — the blast radius of editing it.
 *
 * `explicit` = codes bound to the personal_default campaign id (what
 * post-admin-referral-code writes when no campaign is chosen).
 * `implicit` = promoter/campaign codes with a NULL campaign, which the consumer
 * resolves to personal_default at grant time.
 */
export interface DependentCodeCounts {
  explicit: number;
  implicit: number;
  total: number;
}

/** GET /admin/referrals/settings. */
export interface ReferralSettings {
  /** null only when V102 has not been applied to this schema. */
  personal_default: PromoCampaign | null;
  coins: { signup: number; welcome: number };
  /** 'appconfig' when either key is set there; 'default' when both fall back. */
  coins_source: 'appconfig' | 'default';
  coin_keys: { signup: string; welcome: string };
  dependent_active_codes: DependentCodeCounts;
}

/** PUT /admin/referrals/settings. The endpoint rejects a body with none of these. */
export interface UpdateSettingsBody {
  coins_signup?: number;
  coins_welcome?: number;
  /** Writable here, but owned by the campaign builder — this screen sends neither. */
  name?: string;
  deal_description?: string;
  reason?: string;
}

/**
 * PUT response. `coins.signup` / `coins.welcome` echo ONLY the fields the request
 * sent — an amount left out comes back null, so this is not a snapshot of current
 * state. Refetch instead of rendering it.
 */
export interface SettingsSaveResult {
  applied: string[];
  personal_default: PromoCampaign | null;
  coins: {
    signup: number | null;
    welcome: number | null;
    config_version: number;
    /** false when the requested amounts already matched — no hosted version churned. */
    changed: boolean;
  } | null;
}
